import React from 'react';
import { Link } from 'react-router-dom';
import {
  Archive,
  ArchiveRestore,
  FileText,
  Loader2,
  Pencil,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react';
import VendorMonogram from '../../components/VendorMonogram';
import {
  RequestStatusBadge,
  ReviewStatusBadge,
  RiskRatingBadge,
} from '../../components/RequestBadges';

// Designer layout (2026-06-11): a slim identity header — monogram, vendor
// name, one meta line, actions — with the state read-outs moved to a strip
// of stat cards below (StatStrip) and the process to the rail Progress card.
export default function RequestDetailHeader({
  actionLoading,
  canArchive,
  canCreateContract,
  canDelete,
  canEdit,
  canSubmit,
  canUnarchive,
  canWithdraw,
  handleArchive,
  handleCreateContract,
  handleSubmit,
  handleUnarchive,
  handleWithdraw,
  isArchived,
  onDelete,
  request,
}) {
  const metaParts = [
    request.lightsparkEntity,
    [request.requesterName, request.serviceOwner && request.serviceOwner !== request.requesterName ? request.serviceOwner : null]
      .filter(Boolean).join(' · ') || null,
  ].filter(Boolean);

  return (
    <div className="row" style={{ alignItems: 'flex-start', gap: 16, justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 18 }}>
      <div className="row" style={{ alignItems: 'center', gap: 14, minWidth: 0, flex: '1 1 360px' }}>
        <VendorMonogram name={request.vendorName} size={52} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ marginBottom: 0, fontSize: 26 }}>
              {request.vendorName || 'Untitled request'}
            </h1>
            {isArchived && (
              <span className="chip gray"><Archive className="w-[10px] h-[10px]" /> Archived</span>
            )}
          </div>
          <div className="muted row" style={{ gap: 8, fontSize: 13, flexWrap: 'wrap', marginTop: 4 }}>
            {metaParts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span style={{ opacity: 0.3 }}>·</span>}
                <span>{part}</span>
              </React.Fragment>
            ))}
            {request.vendorWebsite && (
              <>
                {metaParts.length > 0 && <span style={{ opacity: 0.3 }}>·</span>}
                <span className="mono" style={{ color: 'var(--text-secondary)' }}>{request.vendorWebsite}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="row" style={{ gap: 8, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {canEdit && (
          <Link to={`/tprm/${request.id}/edit`} className="btn outline">
            <Pencil className="w-4 h-4" />
            Edit
          </Link>
        )}
        {canSubmit && (
          <button onClick={handleSubmit} disabled={actionLoading} className="btn primary">
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Submit
          </button>
        )}
        {canWithdraw && (
          <button onClick={handleWithdraw} disabled={actionLoading} className="btn outline">
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
            Withdraw
          </button>
        )}
        {canCreateContract && (
          <button onClick={handleCreateContract} disabled={actionLoading} className="btn primary">
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Create contract
          </button>
        )}
        {canArchive && (
          <button onClick={handleArchive} disabled={actionLoading} className="btn outline" title="Hide from the active list (reversible)">
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
            Archive
          </button>
        )}
        {canUnarchive && (
          <button onClick={handleUnarchive} disabled={actionLoading} className="btn outline" title="Restore to the active list">
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArchiveRestore className="w-4 h-4" />}
            Unarchive
          </button>
        )}
        {canDelete && (
          <button
            onClick={onDelete}
            className="icon-btn"
            title="Delete permanently"
            aria-label="Delete permanently"
            style={{ color: 'var(--color-red-500)' }}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

const FEE_SUFFIX = { monthly: '/mo', quarterly: '/qtr', annual: '/yr', one_time: ' one-time' };

// The state read-outs as a row of small stat cards, per the designer mock:
// Status · Overall risk · Legal · Security · Est. fees · Submitted.
export function RequestStatStrip({ request, riskRating, formatCurrency, formatDate }) {
  const stats = [
    { label: 'Status', value: <RequestStatusBadge status={request.status} /> },
    { label: 'Overall risk', value: <RiskRatingBadge rating={riskRating} suffix=" risk" emptyLabel="Unrated" showDot /> },
    { label: 'Legal', value: <ReviewStatusBadge status={request.legalReviewStatus} variant="signoff" /> },
    { label: 'Security', value: <ReviewStatusBadge status={request.securityReviewStatus} variant="signoff" /> },
    {
      label: 'Est. fees',
      value: request.estimatedFees ? (
        <span className="mono" style={{ fontSize: 13.5, color: 'var(--text-primary)', fontWeight: 500 }}>
          {formatCurrency(request.estimatedFees)}
          {FEE_SUFFIX[request.feeFrequency] && (
            <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>{FEE_SUFFIX[request.feeFrequency]}</span>
          )}
        </span>
      ) : (
        <span className="muted" style={{ fontSize: 13 }}>—</span>
      ),
    },
    {
      label: request.submittedAt ? 'Submitted' : 'Drafted',
      value: (
        <span style={{ fontSize: 13.5, color: 'var(--text-primary)', fontWeight: 500 }}>
          {formatDate(request.submittedAt) || formatDate(request.createdAt)}
        </span>
      ),
    },
  ];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 12,
        marginBottom: 20,
      }}
    >
      {stats.map((stat) => (
        <div key={stat.label} className="card" style={{ padding: '12px 14px' }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: 0.7,
              color: 'var(--text-tertiary)',
              marginBottom: 8,
            }}
          >
            {stat.label}
          </div>
          <div style={{ minHeight: 22, display: 'flex', alignItems: 'center' }}>{stat.value}</div>
        </div>
      ))}
    </div>
  );
}
